import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ExpandingTextarea } from "./expanding-textarea";
import { Textarea } from "./textarea";
import MentionPicker from "./mention-picker";
import { cn } from "../../lib/utils";
import { FORMATTED_BODY_CLASS } from "./formatted-body";
import {
  ALL_MENTION_CANDIDATE,
  filterMentionCandidates,
  findMentionTrigger,
  getDisplayMentionParts,
  insertMention,
  normalizeMentionCandidate,
  resyncMentions,
  serializeMentionText,
} from "../../lib/mentions";

const FETCH_DEBOUNCE_MS = 250;
const PICKER_OFFSET_X = -10;
const PICKER_GAP_Y = 10;

const CARET_MIRROR_STYLES = [
  "direction",
  "boxSizing",
  "width",
  "overflowX",
  "overflowY",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderStyle",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "lineHeight",
  "fontFamily",
  "textAlign",
  "textTransform",
  "textIndent",
  "textDecoration",
  "letterSpacing",
  "wordSpacing",
  "tabSize",
  "MozTabSize",
  "whiteSpace",
  "wordBreak",
  "overflowWrap",
  "scrollbarGutter",
];

/**
 * Caret position inside a textarea, relative to its border box.
 * @param {HTMLTextAreaElement} textarea
 * @param {number} position
 * @returns {{ left: number, top: number }}
 */
function getTextareaCaretOffset(textarea, position) {
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");
  mirror.setAttribute("aria-hidden", "true");
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.pointerEvents = "none";
  mirror.style.top = "0";
  mirror.style.left = "-9999px";
  mirror.style.height = "auto";
  for (const name of CARET_MIRROR_STYLES) {
    mirror.style[name] = style[name];
  }
  mirror.style.overflow = "hidden";
  mirror.style.width = `${textarea.offsetWidth}px`;
  const index = Math.max(0, Math.min(position, textarea.value.length));
  mirror.textContent = textarea.value.slice(0, index);
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);
  document.body.appendChild(mirror);
  const contentLeft = marker.offsetLeft - textarea.scrollLeft;
  const contentTop = marker.offsetTop - textarea.scrollTop;
  document.body.removeChild(mirror);
  const borderLeft = parseFloat(style.borderLeftWidth) || 0;
  const paddingLeft = parseFloat(style.paddingLeft) || 0;
  const borderTop = parseFloat(style.borderTopWidth) || 0;
  const paddingTop = parseFloat(style.paddingTop) || 0;
  return {
    left: Math.max(0, borderLeft + paddingLeft + contentLeft),
    top: borderTop + paddingTop + contentTop,
  };
}

const PICKER_VIEWPORT_MARGIN = 8;

/**
 * Viewport coordinates for a mention picker anchored to the caret.
 * Places the list above the caret when there is room, otherwise below.
 *
 * @param {HTMLTextAreaElement} textarea
 * @param {number} caretIndex
 * @param {HTMLElement | null} picker
 * @returns {{ left: number, top: number }}
 */
function getPickerViewportPosition(textarea, caretIndex, picker) {
  const offset = getTextareaCaretOffset(textarea, caretIndex);
  const textareaRect = textarea.getBoundingClientRect();
  const caretLeft = textareaRect.left + offset.left;
  const caretTop = textareaRect.top + offset.top;
  const pickerWidth = picker ? picker.offsetWidth : 192;
  const pickerHeight = picker ? picker.offsetHeight : 0;
  const lineHeight = (() => {
    const raw = parseFloat(window.getComputedStyle(textarea).lineHeight);
    return Number.isFinite(raw) && raw > 0 ? raw : 20;
  })();

  let left = caretLeft + PICKER_OFFSET_X;
  left = Math.min(
    left,
    window.innerWidth - pickerWidth - PICKER_VIEWPORT_MARGIN
  );
  left = Math.max(PICKER_VIEWPORT_MARGIN, left);

  const above = caretTop - pickerHeight - PICKER_GAP_Y;
  if (above >= PICKER_VIEWPORT_MARGIN) {
    return { left, top: above };
  }
  const below = caretTop + lineHeight + PICKER_GAP_Y;
  const maxTop = window.innerHeight - pickerHeight - PICKER_VIEWPORT_MARGIN;
  return {
    left,
    top: Math.max(PICKER_VIEWPORT_MARGIN, Math.min(below, maxTop)),
  };
}

/**
 * Highlighted copy of composer text for the transparent-textarea overlay.
 * @param {Object} props
 * @param {string} props.value
 * @param {Array<{ start: number, end: number }>} props.mentions
 * @returns {JSX.Element | null}
 */
function MentionHighlight({ value, mentions }) {
  if (!value) {
    return null;
  }
  const parts = getDisplayMentionParts(value, mentions);
  return (
    <>
      {parts.map((part, index) =>
        part.type === "mention" ? (
          <span key={index} className="text-accent">
            {part.value}
          </span>
        ) : (
          <React.Fragment key={index}>{part.value}</React.Fragment>
        )
      )}
      {value.endsWith("\n") ? <br /> : null}
    </>
  );
}

/**
 * Textarea with an @mention picker. Inserts display names; call
 * {@code getSerializedValue()} on submit to store stable tokens.
 *
 * @param {Object} props
 * @param {string} props.value
 * @param {(event: React.ChangeEvent<HTMLTextAreaElement>) => void} [props.onChange]
 * @param {Array<object>} [props.candidates]
 * @param {(query: string) => Promise<Array<object>>} [props.fetchCandidates]
 * @param {boolean} [props.includeAll]
 * @param {number} [props.excludeId]
 * @param {boolean} [props.expanding]
 * @param {Array<object>} [props.initialMentions] - Mention ranges for a prefilled value.
 * @param {boolean} [props.autoFocus]
 * @param {() => void} [props.onSubmitEnter]
 * @param {React.KeyboardEventHandler<HTMLTextAreaElement>} [props.onKeyDown]
 * @returns {JSX.Element}
 */
const MentionTextarea = forwardRef(function MentionTextarea(
  {
    value,
    onChange,
    candidates,
    fetchCandidates,
    includeAll = false,
    excludeId,
    expanding = true,
    initialMentions,
    autoFocus,
    onSubmitEnter,
    onKeyDown,
    className,
    ...props
  },
  forwardedRef
) {
  const textareaRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapRef = useRef(null);
  const pickerRef = useRef(null);
  const [mentions, setMentions] = useState(() =>
    Array.isArray(initialMentions) ? initialMentions : []
  );
  const mentionsRef = useRef(mentions);
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [fetched, setFetched] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pickerLeft, setPickerLeft] = useState(0);
  const [pickerTop, setPickerTop] = useState(0);
  const fetchRequestIdRef = useRef(0);

  mentionsRef.current = mentions;

  const staticCandidates = useMemo(() => {
    const list = (candidates || [])
      .map(normalizeMentionCandidate)
      .filter(Boolean)
      .filter((item) => excludeId == null || item.id !== Number(excludeId));
    if (includeAll) {
      return [ALL_MENTION_CANDIDATE, ...list];
    }
    return list;
  }, [candidates, excludeId, includeAll]);

  const usesFetch = typeof fetchCandidates === "function";

  const visibleCandidates = useMemo(() => {
    const query = trigger?.query || "";
    const source = usesFetch ? fetched : staticCandidates;
    return filterMentionCandidates(source, query, 8);
  }, [fetched, staticCandidates, trigger, usesFetch]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [visibleCandidates]);

  useEffect(() => {
    if ((value || "") === "") {
      setMentions([]);
      mentionsRef.current = [];
      setOpen(false);
      setTrigger(null);
    }
  }, [value]);

  useEffect(() => {
    if (!usesFetch || !open || !trigger) {
      return undefined;
    }
    const requestId = ++fetchRequestIdRef.current;
    const query = trigger.query;
    setFetching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await fetchCandidates(query);
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }
        const normalized = (results || [])
          .map(normalizeMentionCandidate)
          .filter(Boolean)
          .filter((item) => excludeId == null || item.id !== Number(excludeId));
        setFetched(normalized);
      } catch (err) {
        if (requestId === fetchRequestIdRef.current) {
          setFetched([]);
        }
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setFetching(false);
        }
      }
    }, FETCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [excludeId, fetchCandidates, open, trigger, usesFetch]);

  const closePicker = useCallback(() => {
    setOpen(false);
    setTrigger(null);
  }, []);

  const syncOverlayScroll = useCallback(() => {
    const textarea = textareaRef.current;
    const overlay = overlayRef.current;
    if (!textarea || !overlay) {
      return;
    }
    overlay.scrollTop = textarea.scrollTop;
    overlay.scrollLeft = textarea.scrollLeft;
  }, []);

  useLayoutEffect(() => {
    syncOverlayScroll();
    const frame = requestAnimationFrame(syncOverlayScroll);
    return () => cancelAnimationFrame(frame);
  }, [value, mentions, syncOverlayScroll]);

  useLayoutEffect(() => {
    if (!autoFocus) {
      return;
    }
    const node = textareaRef.current;
    if (!node) {
      return;
    }
    node.focus({ preventScroll: true });
    const len = (value || "").length;
    if (len > 0) {
      node.setSelectionRange(len, len);
    }
    // Mount-only: put the caret after a prefilled mention.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value/autoFocus are the initial mount snapshot
  }, []);

  useLayoutEffect(() => {
    if (!open || !trigger) {
      return undefined;
    }
    const updatePickerPosition = () => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      const next = getPickerViewportPosition(
        textarea,
        trigger.start,
        pickerRef.current
      );
      setPickerLeft(next.left);
      setPickerTop(next.top);
    };
    updatePickerPosition();
    const frame = requestAnimationFrame(updatePickerPosition);
    const textarea = textareaRef.current;
    window.addEventListener("resize", updatePickerPosition);
    window.addEventListener("scroll", updatePickerPosition, true);
    textarea?.addEventListener("scroll", updatePickerPosition);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePickerPosition);
      window.removeEventListener("scroll", updatePickerPosition, true);
      textarea?.removeEventListener("scroll", updatePickerPosition);
    };
  }, [open, trigger?.start, visibleCandidates]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      syncOverlayScroll();
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, [syncOverlayScroll]);

  const applyMentions = useCallback((nextMentions) => {
    mentionsRef.current = nextMentions;
    setMentions(nextMentions);
  }, []);

  const selectCandidate = useCallback(
    (candidate) => {
      const el = textareaRef.current;
      const caret = el ? el.selectionStart : (trigger?.end ?? (value || "").length);
      const activeTrigger =
        trigger || findMentionTrigger(value || "", caret);
      if (!activeTrigger || !candidate) {
        return;
      }
      const inserted = insertMention(value || "", activeTrigger, candidate);
      const nextMentions = resyncMentions(inserted.text, [
        ...mentionsRef.current.filter(
          (mention) => mention.end <= activeTrigger.start
        ),
        inserted.mention,
      ]);
      applyMentions(nextMentions);
      closePicker();
      const syntheticEvent = {
        target: { value: inserted.text },
      };
      onChange?.(syntheticEvent);
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) {
          return;
        }
        node.focus({ preventScroll: true });
        node.setSelectionRange(inserted.caret, inserted.caret);
      });
    },
    [applyMentions, closePicker, onChange, trigger, value]
  );

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const caret = event.target.selectionStart ?? nextValue.length;
    applyMentions(resyncMentions(nextValue, mentionsRef.current));
    const nextTrigger = findMentionTrigger(nextValue, caret);
    if (nextTrigger) {
      setTrigger(nextTrigger);
      setOpen(true);
    } else {
      closePicker();
    }
    onChange?.(event);
  };

  const handleKeyDown = (event) => {
    if (open) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        if (visibleCandidates.length > 0) {
          setHighlightedIndex((index) =>
            Math.min(index + 1, visibleCandidates.length - 1)
          );
        }
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        if (visibleCandidates.length > 0) {
          setHighlightedIndex((index) => Math.max(index - 1, 0));
        }
        return;
      }
      if (event.key === "Tab" || event.key === "Enter") {
        if (visibleCandidates[highlightedIndex]) {
          event.preventDefault();
          selectCandidate(visibleCandidates[highlightedIndex]);
          return;
        }
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closePicker();
        return;
      }
    }
    onKeyDown?.(event);
  };

  useImperativeHandle(forwardedRef, () => ({
    /**
     * Focus the underlying textarea.
     * @param {FocusOptions} [options]
     * @returns {void}
     */
    focus(options) {
      textareaRef.current?.focus(options);
    },
    /**
     * Serialized body with mention tokens for the API.
     * @returns {string}
     */
    getSerializedValue() {
      return serializeMentionText(value || "", mentionsRef.current);
    },
    /**
     * Mention ranges currently applied to the composer text.
     * @returns {Array<object>}
     */
    getMentions() {
      return mentionsRef.current || [];
    },
    get textarea() {
      return textareaRef.current;
    },
  }));

  const Comp = expanding ? ExpandingTextarea : Textarea;
  const showPicker =
    Boolean(open && trigger) &&
    (!usesFetch || !fetching || visibleCandidates.length > 0);

  return (
    <div ref={wrapRef} className="relative min-w-0 w-full flex-1">
      {showPicker && typeof document !== "undefined"
        ? createPortal(
            <MentionPicker
              ref={pickerRef}
              candidates={visibleCandidates}
              highlightedIndex={highlightedIndex}
              onSelect={selectCandidate}
              style={{ left: pickerLeft, top: pickerTop }}
            />,
            document.body
          )
        : null}
      <div
        ref={overlayRef}
        aria-hidden
        className={cn(
          "rounded-md border border-transparent bg-ui-card px-3 py-2 text-base md:text-sm",
          FORMATTED_BODY_CLASS,
          className,
          "pointer-events-none absolute inset-0 box-border overflow-hidden text-text-primary shadow-none ring-0 focus-visible:ring-0 [scrollbar-gutter:stable]",
          props.disabled && "opacity-50"
        )}
      >
        <MentionHighlight value={value} mentions={mentions} />
      </div>
      <Comp
        {...props}
        ref={textareaRef}
        className={cn(
          className,
          "relative z-[1] bg-transparent text-transparent caret-text-primary [scrollbar-gutter:stable]",
          "[-webkit-text-fill-color:transparent] placeholder:text-ui-muted-foreground placeholder:[-webkit-text-fill-color:var(--ui-muted-foreground)]"
        )}
        value={value}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onScroll={syncOverlayScroll}
        onSubmitEnter={expanding ? onSubmitEnter : undefined}
        aria-autocomplete="list"
        aria-expanded={open}
      />
    </div>
  );
});

MentionTextarea.displayName = "MentionTextarea";

export { MentionTextarea };
